// Navigation for the agenda buttons / links the admin editor inserts into promoted messages,
// info pages, posts etc. The editor serialises them as `internal/agenda…`, `internal/myAgenda…`
// and `internal/session/<id>/<date>/<liveUrl>…`.
//
//   Agenda          web: go to the Program page (no action if already there)
//                   mobile: open the agenda (bottom-nav "Agenda")
//   My agenda       web + mobile: Profile → My programs
//   Specific agenda web: session details popup  mobile: open the agenda, then the details popup

export const AGENDA_LINK_TYPES = ['agenda', 'myAgenda', 'session'];

// Keep in sync with the `max-width: 640px` breakpoint Program.vue uses for the agenda overlay.
const MOBILE_QUERY = '(max-width: 640px)';

export function isAgendaLinkType(type) {
  return AGENDA_LINK_TYPES.includes(type);
}

function isMobileViewport() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(MOBILE_QUERY).matches
  );
}

// Imported lazily: the router pulls in the views, which import the click delegator that calls us.
async function getRouter() {
  const { default: router } = await import('@/router');
  return router;
}

// The router keeps the previous page's scroll offset on a fresh navigation, so landing on the
// Program page from a scrolled lobby / info page would open it part-way down.
async function pushToProgram(router, location) {
  await router.push(location);
  window.scrollTo({ top: 0 });
}

function ignoreDuplicateNavigation() {
  /* navigating to the current location is harmless */
}

// `parts` is the editor value split on "/": ['internal', type, sessionId?, date?, …].
// Returns true when the link was an agenda link and has been handled.
export function handleAgendaLink(parts) {
  const type = parts && parts[1];
  if (!isAgendaLinkType(type)) {
    return false;
  }

  const sessionId = type === 'session' && parts[2] ? String(parts[2]) : null;
  openAgendaLink(type, sessionId).catch(ignoreDuplicateNavigation);
  return true;
}

export async function openAgendaLink(type, sessionId = null) {
  const router = await getRouter();
  const root = router.app && router.app.$root;
  const mobile = isMobileViewport();
  const onProgram = router.currentRoute.name === 'program';

  if (type === 'myAgenda') {
    if (router.currentRoute.name === 'profile' && root) {
      // Already mounted: the hash alone wouldn't change, so ask the page to switch tab.
      root.$emit('profile:open-programs');
      return;
    }
    await router.push({ path: '/profile', hash: '#my-programs' });
    return;
  }

  if (type === 'agenda') {
    if (onProgram) {
      // Mobile opens the agenda via the bottom-nav panel; web already shows it inline, so
      // just bring the page back to the top.
      if (mobile) {
        if (root) {
          // Leave any open side-panel section (e.g. Posts) first.
          root.$emit('live-stream:set-tab', null);
          root.$emit('program:open-agenda');
        }
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }
    await pushToProgram(
      router,
      mobile ? { name: 'program', query: { agenda: '1' } } : { name: 'program' }
    );
    return;
  }

  // type === 'session'
  if (!sessionId) {
    return;
  }
  if (onProgram && root) {
    if (mobile) {
      root.$emit('live-stream:set-tab', null);
      root.$emit('program:open-agenda');
    }
    root.$emit('program:open-session', sessionId);
    return;
  }
  // `agenda=1` opens the mobile agenda panel and lets the page through even when the
  // Main stage page is switched off (see Program.enforceMainStagePage).
  await pushToProgram(router, {
    name: 'program',
    query: mobile
      ? { agenda: '1', sessionDetails: sessionId }
      : { sessionDetails: sessionId }
  });
}
